import React, { useState } from 'react';
import type { CharacterPortrait, SituationSortGameScene } from '../../types';
import { getSituationSortDisplayLabel } from '../../utils/situation-sort-label';
import { FormattedInlineText } from '../formatted-inline-text';
import { SituationSortGameEditor } from './SituationSortEditors';
import { SortCategoriesModal } from './SortCategoriesModal';
import { InlineEditableText } from './InlineEditableText';
import type { SortCategoryActions } from './sort-category-actions';
import { getContentItemSummary } from './utils';
import inline from './inline-editor.module.css';
import studio from './scenario-studio.module.css';

interface InlineSituationSortGameEditorProps {
  item: SituationSortGameScene;
  sceneId: string;
  characters: CharacterPortrait[];
  editMode: boolean;
  onChange: (item: SituationSortGameScene) => void;
  sortCategoryActions?: SortCategoryActions;
}

export const InlineSituationSortGameEditor: React.FC<InlineSituationSortGameEditorProps> = ({
  item,
  sceneId,
  characters,
  editMode,
  onChange,
  sortCategoryActions,
}) => {
  const [categoriesModalOpen, setCategoriesModalOpen] = useState(false);

  if (!editMode) {
    return (
      <div className={inline.situationSortReadonly}>
        {item.instructionText && (
          <span className={inline.slText}>{item.instructionText}</span>
        )}
        {(item.speakerName || item.speakerCharacterId) && (
          <span className={inline.situationSortSpeaker}>
            {item.speakerName || item.speakerCharacterId}
          </span>
        )}
        <span className={inline.situationSortMeta}>{getContentItemSummary(item)}</span>
        {item.categories.length > 0 && (
          <div className={inline.situationSortCategories}>
            {item.categories.map((cat) => (
              <span key={cat.id} className={inline.situationSortCategoryChip}>
                <FormattedInlineText text={cat.label || cat.id} />
              </span>
            ))}
          </div>
        )}
        {item.situations.length > 0 && (
          <div className={inline.situationSortSituations}>
            {item.situations.map((situation, index) => (
              <span key={situation.id} className={inline.situationSortSituationChip}>
                {getSituationSortDisplayLabel(situation, index)}
              </span>
            ))}
          </div>
        )}
        {item.requireAllCorrect && (
          <span className={inline.situationSortMeta}>Toutes les réponses requises</span>
        )}
      </div>
    );
  }

  return (
    <div className={inline.situationSortEditor}>
      {sortCategoryActions && (
        <div className={inline.situationSortGlobalActions}>
          <button
            type="button"
            className={studio.btnSecondary}
            onClick={() => setCategoriesModalOpen(true)}
          >
            Gérer les catégories du scénario
            {sortCategoryActions.globalCategories.length > 0
              ? ` (${sortCategoryActions.globalCategories.length})`
              : ''}
          </button>
          <SortCategoriesModal
            open={categoriesModalOpen}
            onClose={() => setCategoriesModalOpen(false)}
            sortCategoryActions={sortCategoryActions}
          />
        </div>
      )}
      {item.categories.length > 0 && (
        <div className={inline.situationSortCategories}>
          {item.categories.map((cat, index) => (
            <span key={cat.id} className={inline.situationSortCategoryEdit}>
              <span className={inline.situationSortCategoryEditLabel}>Cat. {index + 1}</span>
              <span className={inline.situationSortCategoryPreviewOnly}>
                <FormattedInlineText text={cat.label || cat.id} />
              </span>
            </span>
          ))}
        </div>
      )}
      {item.situations.length > 0 && (
        <div className={inline.situationSortSituations}>
          {item.situations.map((situation, index) => (
            <label key={situation.id} className={inline.situationSortSituationEdit}>
              <span className={inline.situationSortSituationEditLabel}>
                {getSituationSortDisplayLabel(situation, index)}
              </span>
              <InlineEditableText
                value={situation.label ?? ''}
                editMode
                className={inline.situationSortSituationInput}
                onChange={(label) => {
                  const situations = item.situations.map((s, i) =>
                    i === index ? { ...s, label: label.trim() || undefined } : s
                  );
                  onChange({ ...item, situations });
                }}
                placeholder={`Libellé carte (défaut : Situation ${index + 1})`}
              />
            </label>
          ))}
        </div>
      )}
      <SituationSortGameEditor
        item={item}
        characters={characters}
        sceneId={sceneId}
        sortCategoryActions={sortCategoryActions}
        onChange={(patch) => onChange({ ...item, ...patch })}
      />
    </div>
  );
};
